"use client";

import * as React from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export type DatePickerProps = {
  date?: Date;
  onSelect?: (date: Date | undefined) => void;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  modal?: boolean;
  fromDate?: Date;
  toDate?: Date;
};

export function DatePicker({
  date,
  onSelect,
  className,
  disabled = false,
  placeholder = "Pick a date",
  modal = false,
  fromDate,
  toDate,
}: DatePickerProps) {
  const [selectedDate, setSelectedDate] = React.useState<Date | undefined>(
    date
  );
  const [open, setOpen] = React.useState(false);

  // Update internal state when date prop changes
  React.useEffect(() => {
    setSelectedDate(date);
  }, [date]);

  const handleSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    setOpen(false);
    if (onSelect) {
      onSelect(date);
    }
  };

  return (
    <Popover modal={modal} open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          rounded="full"
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal border-input",
            !selectedDate && "text-muted-foreground",
            className
          )}
          disabled={disabled}
        >
          {selectedDate ? format(selectedDate, "PPP") : placeholder}
          <CalendarIcon className="ml-auto h-4 w-4 text-gray-500" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          disabled={[
            ...(fromDate ? [{ before: fromDate }] : []),
            ...(toDate ? [{ after: toDate }] : []),
          ]}
          mode="single"
          selected={selectedDate}
          onSelect={handleSelect}
          className="p-3"
        />
      </PopoverContent>
    </Popover>
  );
}
